import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { PhotoViewer } from "./PhotoViewer";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, DEFAULT_CATEGORIES, SEASONS, prettyDate, ageLabel,
  categoriesIn, todayLocal, fitCheck,
  type Child, type SizeRecord, type InventoryItem, type ChildSignals, type FitCheckResult,
} from "./store";
import { sizingRecap, askSizes, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, Lock, WifiOff, RefreshCw, Plus,
  Send, ShoppingBag,
} from "lucide-react";
import appIcon from "./assets/app-icon.svg";
import catShoes from "./assets/cat-shoes.svg";
import catShoesDark from "./assets/cat-shoes-dark.svg";
import catTops from "./assets/cat-tops.svg";
import catTopsDark from "./assets/cat-tops-dark.svg";
import catTrousers from "./assets/cat-trousers.svg";
import catTrousersDark from "./assets/cat-trousers-dark.svg";
import catJackets from "./assets/cat-jackets.svg";
import catJacketsDark from "./assets/cat-jackets-dark.svg";
import glyphGrowth from "./assets/glyph-growth-record.svg";
import glyphGrowthDark from "./assets/glyph-growth-record-dark.svg";
import emptyNoChildren from "./assets/empty-no-children.svg";
import emptyNoChildrenDark from "./assets/empty-no-children-dark.svg";
import emptyNoSizes from "./assets/empty-no-sizes.svg";
import emptyNoSizesDark from "./assets/empty-no-sizes-dark.svg";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

// Drawn category marks (graphite / parchment colourways) — the identity's replacement for the
// emoji category icons. Both variants render; CSS shows one per colour scheme.
const CAT_MARKS: Record<string, [string, string]> = {
  shoes: [catShoes, catShoesDark],
  tops: [catTops, catTopsDark],
  trousers: [catTrousers, catTrousersDark],
  jackets: [catJackets, catJacketsDark],
  coats: [catJackets, catJacketsDark],
};
function CatMark({ category, size = 22 }: { category: string; size?: number }) {
  const pair = CAT_MARKS[(category || "").trim().toLowerCase()];
  if (!pair) {
    return (
      <span className="cat-mark" aria-hidden style={{ width: size, height: size, display: "inline-grid", placeItems: "center", fontWeight: 700, fontSize: Math.round(size * 0.5), color: "var(--ink-3)", border: "1.5px solid var(--border-strong)", borderRadius: 6, flexShrink: 0 }}>
        {((category || "?").trim()[0] || "?").toUpperCase()}
      </span>
    );
  }
  return (
    <span className="cat-mark" aria-hidden style={{ width: size, height: size, flexShrink: 0 }}>
      <img className="mark-light" src={pair[0]} width={size} height={size} alt="" />
      <img className="mark-dark" src={pair[1]} width={size} height={size} alt="" />
    </span>
  );
}
// Light/dark pair for a delivered illustration or glyph.
function ThemedArt({ light, dark, className }: { light: string; dark: string; className?: string }) {
  return (
    <span className={className}>
      <img className="mark-light" src={light} alt="" />
      <img className="mark-dark" src={dark} alt="" />
    </span>
  );
}

/**
 * 'what's already bought, waiting to be grown into' — sizes climbing a rule.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function RuleMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 20V4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M5 17h5M5 12.6h8M5 8.2h5M5 4h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-outgrown://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-outgrown://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Rightsize"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <img src={appIcon} width={48} height={48} alt="" style={{ borderRadius: 11, marginBottom: "0.4rem" }} />
        <div style={{ color: "var(--accent-deep)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Rightsize</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your children's sizes live on
          this device; the Sizing Recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-outgrown");
              else if (p === "macos") await signInMac("oriora-outgrown");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-outgrown/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [children, setChildren] = useState<Child[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [records, setRecords] = useState<SizeRecord[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [showAddChild, setShowAddChild] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [showLogSize, setShowLogSize] = useState(false);
  // Set when the log sheet is opened with values to prefill — from a fit-check ("log this size")
  // or a current-sizes row tap (category only). Cleared on close.
  const [logPrefill, setLogPrefill] = useState<{ category?: string; brand?: string; sizeLabel?: string; sizeValue?: string } | null>(null);
  const [editingSize, setEditingSize] = useState<SizeRecord | null>(null);
  const [showAddInv, setShowAddInv] = useState(false);
  const [editingInv, setEditingInv] = useState<InventoryItem | null>(null);
  const [confirmDelSize, setConfirmDelSize] = useState<SizeRecord | null>(null);
  const [confirmDelInv, setConfirmDelInv] = useState<InventoryItem | null>(null);
  const [confirmDelChild, setConfirmDelChild] = useState<Child | null>(null);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — no existing
  // screen changes. onChanged → reloadData() refreshes this child's sizes + inventory after a
  // chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const active = useMemo(() => children.find((c) => c.id === activeId) ?? null, [children, activeId]);
  const recapRef = useRef<HTMLDivElement>(null);
  const growthRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const loadChildren = useCallback(async () => {
    const list = await store.listChildren();
    setChildren(list);
    setActiveId((cur) => (cur && list.some((c) => c.id === cur) ? cur : (list[0]?.id ?? null)));
    setLoaded(true);
  }, []);
  useEffect(() => { void loadChildren(); }, [loadChildren]);

  const loadChildData = useCallback(async (childId: string | null) => {
    if (!childId) { setRecords([]); setInventory([]); return; }
    const [recs, inv] = await Promise.all([store.listSizeRecords(childId), store.listInventory(childId)]);
    setRecords(recs);
    setInventory(inv);
  }, []);
  useEffect(() => { void loadChildData(activeId); }, [activeId, loadChildData]);

  const reloadAll = useCallback(async () => {
    await loadChildren();
  }, [loadChildren]);
  const reloadData = useCallback(async () => { await loadChildData(activeId); }, [activeId, loadChildData]);

  const knownCategories = useMemo(() => {
    const fromData = categoriesIn(records);
    const merged = [...fromData];
    for (const c of DEFAULT_CATEGORIES) if (!merged.some((x) => x.toLowerCase() === c.toLowerCase())) merged.push(c);
    return merged;
  }, [records]);

  // ── FIT BANDS (ledger row 2b). The object here is not a garment, it is the FIT RELATIONSHIP
  // between a garment and a child who keeps growing — and the app already derives its state
  // ("outgrown" / "fits now" / "ahead"), computing it inside each tile and printing it as a pill
  // while the grid stayed in category A-Z, size-ascending CATALOGUE order. So the screen answered
  // "what do I own" when the only questions that matter are "what can I pass on", "what fits right
  // now" and "what is waiting". That is the ledger's 4-for-4 pattern — decision data computed, then
  // thrown away by the ordering.
  //
  // The thresholds match InventoryTile's exactly (±0.25 of a size) so a tile can never disagree
  // with the band it sits in.
  const fitBands = useMemo(() => {
    const of = (i: InventoryItem) => {
      const cur = records.find((r) => r.category.toLowerCase() === i.category.toLowerCase() && r.size_value != null);
      const currentValue = cur?.size_value ?? null;
      const ahead = (i.size_value != null && currentValue != null)
        ? Math.round((i.size_value - currentValue) * 100) / 100
        : null;
      const key = ahead == null ? "unknown" : ahead > 0.25 ? "ahead" : ahead < -0.25 ? "outgrown" : "now";
      return { item: i, currentValue, ahead, key };
    };
    const scored = inventory.map(of);
    const band = (key: string, title: string, hint: string) => ({
      key, title, hint,
      // Within a band, the most decision-relevant first: the most outgrown, the closest to fitting.
      items: scored
        .filter((x) => x.key === key)
        .sort((a, b) => {
          if (a.ahead == null || b.ahead == null) return 0;
          return key === "outgrown" ? a.ahead - b.ahead : Math.abs(a.ahead) - Math.abs(b.ahead);
        }),
    });
    // The shelf is titled "Ready for next", so the bought-ahead band leads (the design's order);
    // outgrown keeps its pass-on framing further down.
    return [
      band("ahead", "Ready — bought ahead", "Waiting for the next size to arrive."),
      band("now", "Fits now", ""),
      band("outgrown", "Outgrown — ready to pass on", "Smaller than the size last recorded for that category."),
      band("unknown", "No size logged yet", "Add a size for this category to place these."),
    ].filter((b) => b.items.length > 0);
  }, [inventory, records]);

  const doExport = useCallback(() => {
    if (!active || (records.length === 0 && inventory.length === 0)) return;
    const { csv, filename } = buildExport(active, records, inventory);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [active, records, inventory]);
  exportRef.current = doExport;

  // Native-menu actions (macOS Actions menu): Log / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setEditingSize(null); setShowLogSize(true); break;
        case "recap":
          recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          recapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  async function delSize(id: string) { setConfirmDelSize(null); await store.deleteSizeRecord(id); await reloadData(); }
  async function delInv(id: string) { setConfirmDelInv(null); await store.deleteInventoryItem(id); await reloadData(); }
  async function delChild(id: string) {
    setConfirmDelChild(null);
    await store.deleteChild(id);
    await reloadAll();
  }

  if (!loaded) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;

  return (
    <main style={{
      maxWidth: 760, margin: "0 auto",
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(5.5rem + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--warning)", background: "rgba(201,138,58,0.12)", border: "1px solid rgba(201,138,58,0.35)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Sizing Recap paused — your entries still save locally
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
        <div className="row" style={{ gap: "0.5rem" }}>
          <img src={appIcon} width={24} height={24} alt="" style={{ borderRadius: 6 }} />
          <h1 className="serif" style={{ fontSize: "1.5rem", margin: 0 }}>Rightsize</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Rightsize" fileSlug="outgrown" appScheme="oriora-outgrown"
            hasData={children.length > 0} onRestored={async () => { await reloadAll(); await reloadData(); }}
          />
          <AccountMenu email={email} appSlug="oriora-outgrown" appName="Oriora Rightsize" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {children.length === 0 ? (
        <EmptyChildren onAdd={() => { setEditingChild(null); setShowAddChild(true); }}
          onStarted={async (name, category, size, brand) => {
            const child = await store.createChild({ name });
            await store.createSizeRecord({ child_id: child.id, category, size_label: size, brand });
            await reloadAll();
          }} />
      ) : (
        <>
          <ChildSwitcher
            children={children}
            activeId={activeId}
            onPick={setActiveId}
            onAdd={() => { setEditingChild(null); setShowAddChild(true); }}
          />

          {active && (
            <>
              <FitCheckCard
                child={active}
                records={records}
                inventory={inventory}
                categories={knownCategories}
                onLog={(prefill) => { setEditingSize(null); setLogPrefill(prefill); setShowLogSize(true); }}
                onGrowth={() => growthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                onReady={() => readyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                onEdit={() => { setEditingChild(active); setShowAddChild(true); }}
                onDelete={() => setConfirmDelChild(active)}
              />

              {/* The growth record — sizes over time as doorframe pencil marks */}
              <div ref={growthRef}>
                <SectionHeader
                  title="The growth record"
                  count={records.length}
                  action={<>
                    {records.length > 0 && <button className="btn btn-sm" onClick={doExport}>Export CSV</button>}
                  </>}
                />
                {records.length === 0 ? (
                  <div className="card" style={{ padding: "1.75rem 1.25rem", textAlign: "center" }}>
                    <ThemedArt light={emptyNoSizes} dark={emptyNoSizesDark} className="empty-art" />
                    <div className="serif" style={{ fontSize: "1.1rem", margin: "0.4rem 0 0.2rem" }}>No sizes logged yet for {active.name}</div>
                    <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "1rem" }}>Log the first one off any label — shoes, a top, anything.</div>
                    <button className="btn btn-primary row" style={{ gap: "0.4rem", margin: "0 auto" }} onClick={() => { setEditingSize(null); setLogPrefill(null); setShowLogSize(true); }}>
                      <Plus size={15} /> Log a size
                    </button>
                  </div>
                ) : (
                  <GrowthRecord
                    records={records}
                    onEdit={(r) => { setEditingSize(r); setLogPrefill(null); setShowLogSize(true); }}
                    onDelete={setConfirmDelSize}
                  />
                )}
              </div>

              {/* Ready for next — the bought-ahead shelf */}
              <div ref={readyRef}>
                <SectionHeader
                  title="Ready for next"
                  count={inventory.length}
                  action={<button className="btn btn-sm row" style={{ gap: "0.3rem" }} onClick={() => { setEditingInv(null); setShowAddInv(true); }}><ShoppingBag size={12} /> Add item</button>}
                />
                {inventory.length === 0 ? (
                  <div className="card" style={{ padding: "1.5rem 1.25rem", textAlign: "center" }}>
                    <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>What's already bought, waiting to be grown into — add pieces here and they're matched against {active.name}'s current sizes.</div>
                  </div>
                ) : (
                  <div className="ready-shelf">
                    <div className="ready-shelf-hint">What's already bought, waiting to be grown into.</div>
                    {fitBands.map((band) => (
                      <section key={band.key}>
                        <div className={`ready-shelf-head${band.key === "ahead" ? " ready-item-lead" : ""}`}>
                          <span className="ready-shelf-name">{band.title}</span>
                          <span className="tnum muted" style={{ fontSize: "var(--fs-xs)" }}>{band.items.length}</span>
                        </div>
                        {band.hint && <div className="ready-shelf-hint">{band.hint}</div>}
                        <div className="ready-grid">
                          {band.items.map(({ item, currentValue }) => (
                            <InventoryTile key={item.id} i={item} currentValue={currentValue}
                              onEdit={() => { setEditingInv(item); setShowAddInv(true); }}
                              onDelete={() => setConfirmDelInv(item)} />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>

              <div ref={recapRef}>
                <SizingRecapCard child={active} records={records} inventory={inventory} />
              </div>

              <AskCard child={active} records={records} inventory={inventory} />
            </>
          )}
        </>
      )}


      {/* ── Modals ── */}
      {showAddChild && (
        <ChildForm
          editing={editingChild}
          onClose={() => { setShowAddChild(false); setEditingChild(null); }}
          onSaved={async (savedId) => { setShowAddChild(false); setEditingChild(null); await reloadAll(); if (savedId) setActiveId(savedId); }}
        />
      )}
      {showLogSize && active && (
        <SizeForm
          child={active}
          editing={editingSize}
          initialCategory={logPrefill?.category ?? null}
          initialBrand={logPrefill?.brand ?? ""}
          initialSizeLabel={logPrefill?.sizeLabel ?? ""}
          initialSizeValue={logPrefill?.sizeValue ?? ""}
          categories={knownCategories}
          onClose={() => { setShowLogSize(false); setEditingSize(null); setLogPrefill(null); }}
          onSaved={async () => { setShowLogSize(false); setEditingSize(null); setLogPrefill(null); await reloadData(); }}
        />
      )}
      {showAddInv && active && (
        <InventoryForm
          child={active}
          editing={editingInv}
          categories={knownCategories}
          onClose={() => { setShowAddInv(false); setEditingInv(null); }}
          onSaved={async () => { setShowAddInv(false); setEditingInv(null); await reloadData(); }}
        />
      )}

      {confirmDelSize && (
        <ConfirmDelete
          title="Delete this size?"
          body={`Delete ${active?.name ?? "this child"}'s ${confirmDelSize.category} size ${confirmDelSize.size_label} (${prettyDate(confirmDelSize.recorded_on)})? This can't be undone.`}
          onCancel={() => setConfirmDelSize(null)}
          onConfirm={() => delSize(confirmDelSize.id)}
        />
      )}
      {confirmDelInv && (
        <ConfirmDelete
          title="Delete this item?"
          body={`Remove the ${confirmDelInv.category} (size ${confirmDelInv.size_label}) from bought-ahead? This can't be undone.`}
          onCancel={() => setConfirmDelInv(null)}
          onConfirm={() => delInv(confirmDelInv.id)}
        />
      )}
      {confirmDelChild && (
        <ConfirmDelete
          title={`Delete ${confirmDelChild.name}?`}
          body={`This removes ${confirmDelChild.name} and ALL their size records and bought-ahead items from this device. This can't be undone.`}
          onCancel={() => setConfirmDelChild(null)}
          onConfirm={() => delChild(confirmDelChild.id)}
        />
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the
          active child's sizes + inventory. Scoped to `active` — needs a child to act on. */}
      {!chatOpen && (
        <button className="otg-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <RuleMark size={19} />
        </button>
      )}
      <aside className={`otg-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="otg-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, fix, or ask</span>
          <button className="btn btn-sm" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        {/* Mount the chat even with no child. Gating the whole panel left the customer's own
            library unreachable on a fresh install; size actions still ask for a child.
            🔴 G25 — MOUNTED UNCONDITIONALLY, visibility is the aside's CSS class + aria-hidden above.
            The old shape gated this line on the open flag, which unmounts the component the moment the
            sheet closes: React tears down the in-flight request, the assistant's reply is lost, and
            a HELD DESTRUCTIVE-ACTION CONFIRM is destroyed so it can never be tapped. The rule is
            "it works until it has finished what I told it to do" (Bogdan, 2026-07-13) — fixed
            fleet-wide then, but Outgrown shipped this on 2026-07-27, two weeks after, and was the
            only live app still carrying it. Never gate the chat on the MOUNT. */}
        <Chat childId={active?.id ?? ""} childName={active?.name ?? ""} onChanged={reloadData} />
      </aside>
    </main>
  );
}

// ── READY-STATE 2026-09-19 — the one-line door to the first size entry ──────────────────────
// Name, one category, the size on the label, and the brand — because the brand is the half that
// makes the record worth keeping. Two existing store writes, no AI, no forecast.
const RS_FIRST_CATEGORIES = ["Shoes", "Tops", "Bottoms", "Coats"] as const;

function FirstSize({ onStarted, onFullForm }: {
  onStarted: (name: string, category: string, size: string, brand: string) => Promise<void>;
  onFullForm: () => void;
}) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<string>("Shoes");
  const [size, setSize] = useState("");
  const [brand, setBrand] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    const n = name.trim(), sz = size.trim();
    if (!n) { setErr("Whose record is this? A first name is enough."); return; }
    if (!sz) { setErr("The size on the label — anything they are wearing today."); return; }
    setBusy(true); setErr(null);
    try { await onStarted(n, category, sz, brand.trim()); }
    catch (e) { setErr(`Couldn't start the record — ${e instanceof Error ? e.message : String(e)}`); setBusy(false); }
  }

  return (
    <div className="rs-first">
      <div className="rs-first-rule"><span>One size to start</span></div>
      <div className="rs-first-row">
        <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Their name"
          placeholder="Their name" className="rs-first-name"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void go(); } }} />
        <input value={size} onChange={(e) => setSize(e.target.value)} aria-label="The size on the label"
          placeholder="size" className="rs-first-size"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void go(); } }} />
      </div>
      <input value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand"
        placeholder="Brand — the half that makes a 6 mean something" className="rs-first-brand"
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void go(); } }} />
      <div className="rs-first-chips" role="group" aria-label="Category">
        {RS_FIRST_CATEGORIES.map((c) => (
          <button key={c} type="button" className={`chip${category === c ? " selected" : ""}`}
            onClick={() => setCategory(c)}>{c}</button>
        ))}
      </div>
      {err && <div className="rs-first-err">{err}</div>}
      <button className="btn btn-primary rs-first-go" disabled={busy} onClick={() => void go()}>
        {busy ? "Starting…" : "Start the record"}
      </button>
      <p className="rs-first-hint">
        One entry is a starting point, not a trend — the verdict gets sharper with every size you
        log, and nothing is guessed in between.
        {" "}
        <button type="button" className="rs-first-alt" onClick={onFullForm}>Add with birth year instead</button>
      </p>
    </div>
  );
}

// ── Empty state: no children yet ─────────────────────────────────────────────
// READY-STATE 2026-09-19 — the first screen was the art, a sentence and "Add a child", so the
// two things the matrix calls this app's spine were invisible: the buying-moment VERDICT
// (buy-ahead / fits / outgrown — no competitor leads with a decision over your own ledger) and
// the BRAND-FIT MEMORY it rests on (a 6 in one brand is not a 6 in another). Both are now on the
// screen before an entry exists, and one line starts the record.
// ⚠️ HONEST AT ONE ENTRY: a single point is not a trend, so nothing here forecasts a next size —
// the card says what the ledger can actually support and no more.
function EmptyChildren({ onAdd, onStarted }: { onAdd: () => void; onStarted?: (name: string, category: string, size: string, brand: string) => Promise<void> }) {
  return (
    <div className="card" style={{ padding: "1.6rem 1.35rem" }}>
      <ThemedArt light={emptyNoChildren} dark={emptyNoChildrenDark} className="empty-art" />
      <div className="serif rs-first-head">Start the record</div>
      <p className="rs-first-lede">
        Standing in the shop with something in your hand, this answers one question — and the
        answer comes from what <strong>you</strong> logged, because a 6 in one brand is not a 6 in
        another.
      </p>

      <div className="rs-verdicts" aria-label="The three verdicts">
        <div className="rs-verdict"><span className="rs-v-k">Buy ahead</span><span className="rs-v-d">bigger than they wear now — it will fit later</span></div>
        <div className="rs-verdict is-now"><span className="rs-v-k">Fits</span><span className="rs-v-d">this brand, this size, right now</span></div>
        <div className="rs-verdict"><span className="rs-v-k">Outgrown</span><span className="rs-v-d">under what they are already in — leave it</span></div>
      </div>

      {onStarted ? <FirstSize onStarted={onStarted} onFullForm={onAdd} />
        : (
          <button className="btn btn-primary row" style={{ gap: "0.4rem", margin: "1rem auto 0" }} onClick={onAdd}>
            <Plus size={16} /> Add a child
          </button>
        )}

      <p className="rs-first-foot">
        {/* §7b: "no account" was FALSE — sign-in is required (App.tsx:178 `if (!authed) return <Login />`)
            — and "stays on this device" was unqualified while Cloud Backup ships. Rewritten 2026-09-25.
            "nothing tracked" is kept as "no analytics": grep for analytics|telemetry|posthog|mixpanel|
            amplitude|sentry|gtag|beacon across src/ and src-tauri/src/ returns zero hits. */}
        Every size they've worn, and every size waiting for them,
        stays on this device unless you back it up — the backup goes to your own cloud, not ours.
        The app carries no analytics.
      </p>
    </div>
  );
}

// ── Child switcher ────────────────────────────────────────────────────────────
function ChildSwitcher({ children, activeId, onPick, onAdd }: {
  children: Child[]; activeId: string | null; onPick: (id: string) => void; onAdd: () => void;
}) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <div className="seg">
        {children.map((c) => (
          <button key={c.id} className={`seg-item${c.id === activeId ? " selected" : ""}`} onClick={() => onPick(c.id)}>
            {c.name}
          </button>
        ))}
        <button className="seg-item seg-add" onClick={onAdd} aria-label="Add a child"><Plus size={14} /></button>
      </div>
    </div>
  );
}

// ── Fit check — the buying-moment verdict that LEADS the home screen ──────────
// The app's spine: "this brand's size X, for this child, right now — buy-ahead, fits now, or
// already outgrown?" answered ONLY from the sizes the parent has logged (store.fitCheck). No
// growth model, no prediction, no health — the competitor lane forecasts; Rightsize does not.
// Below the verdict: a compact current-sizes reference (the ledger the verdict draws on) and the
// links down to the growth record + bought-ahead shelf (both demoted to receipts).
const VERDICT_LABEL: Record<FitCheckResult["verdict"], string> = {
  "buy-ahead": "Buy-ahead",
  "fits-now": "Fits now",
  "outgrown": "Already outgrown",
  "unknown": "Not enough to say yet",
};

function verdictSentence(res: FitCheckResult, name: string, category: string, brand: string, sizeLabel: string): string {
  const cat = category.trim().toLowerCase() || "size";
  const who = brand.trim() ? `${brand.trim()}'s ${sizeLabel.trim()}` : `A ${sizeLabel.trim()}`;
  const cur = res.currentLabel ?? "";
  if (res.verdict === "unknown") {
    return res.reason === "no-number"
      ? `Add a number in the size (e.g. 116, EU 32) so Rightsize can compare it against ${name}'s logged ${cat} sizes.`
      : `No ${cat} size logged for ${name} yet. Log one and Rightsize can check this fit.`;
  }
  if (res.verdict === "fits-now") return `${who} lines up with ${name}'s current ${cat} size (${cur}) — fits now.`;
  if (res.verdict === "outgrown") return `${who} is below ${name}'s current ${cat} size (${cur}) — ${name} is past it.`;
  const ahead = res.delta != null ? Math.round(res.delta * 10) / 10 : null;
  const aheadTxt = ahead != null ? `about ${ahead} size${ahead === 1 ? "" : "s"} ` : "";
  return `${who} is ${aheadTxt}above ${name}'s current ${cat} size (${cur}) — a buy-ahead, not a fit yet.`;
}

function FitCheckCard({ child, records, inventory, categories, onLog, onGrowth, onReady, onEdit, onDelete }: {
  child: Child; records: SizeRecord[]; inventory: InventoryItem[]; categories: string[];
  onLog: (prefill: { category?: string; brand?: string; sizeLabel?: string; sizeValue?: string }) => void;
  onGrowth: () => void; onReady: () => void; onEdit: () => void; onDelete: () => void;
}) {
  // Latest record per category — picked by recorded_on (native returns newest-first, but the web
  // WebStore returns insertion order, so choose by date to be correct on both surfaces).
  const current = useMemo(() => {
    const byCat = new Map<string, SizeRecord>();
    for (const r of records) {
      const k = r.category.toLowerCase();
      const cur = byCat.get(k);
      if (!cur || r.recorded_on > cur.recorded_on || (r.recorded_on === cur.recorded_on && r.created_at > cur.created_at)) byCat.set(k, r);
    }
    return [...byCat.values()].sort((a, b) => a.category.localeCompare(b.category));
  }, [records]);

  // Nearest bought-ahead item per category — same +0.25 threshold as the fit bands.
  const nextFor = useMemo(() => {
    const map = new Map<string, InventoryItem>();
    for (const r of current) {
      if (r.size_value == null) continue;
      const candidates = inventory
        .filter((i) => i.category.toLowerCase() === r.category.toLowerCase() && i.size_value != null && i.size_value - r.size_value! > 0.25)
        .sort((a, b) => a.size_value! - b.size_value!);
      if (candidates[0]) map.set(r.category.toLowerCase(), candidates[0]);
    }
    return map;
  }, [current, inventory]);

  const age = ageLabel(child.birth_year);

  // Fit-check form state. Default the category to the first one the child already has sizes in,
  // so a check lands on a category with a reference to measure against.
  const [category, setCategory] = useState(current[0]?.category ?? categories[0] ?? "Shoes");
  const [brand, setBrand] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const asked = sizeLabel.trim() !== "";
  const res = useMemo(() => fitCheck(records, category, brand, sizeLabel), [records, category, brand, sizeLabel]);

  return (
    <div className="hero" style={{ display: "block", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.9rem" }}>
        <div>
          <div className="serif" style={{ fontSize: "1.7rem", fontWeight: 600, lineHeight: 1.1 }}>{child.name}</div>
          <div style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", marginTop: 2 }}>
            {age ? age : "Sizes tracked here"}{current.length > 0 ? ` · ${current.length} categor${current.length === 1 ? "y" : "ies"} tracked` : ""}
          </div>
        </div>
        <div className="row" style={{ gap: "0.3rem" }}>
          <button className="btn btn-on-hero btn-sm" onClick={onEdit}>Edit</button>
          <button className="btn btn-on-hero btn-sm" title="Delete child" aria-label="Delete child" onClick={onDelete}><Trash2 size={13} /></button>
        </div>
      </div>

      {/* The spine: will this size fit? */}
      <div className="fitcheck">
        <div className="fitcheck-title serif">Will this size fit?</div>
        <div className="fitcheck-sub">Check a brand and size against what you've logged for {child.name}.</div>

        <div className="fitcheck-cats">
          {categories.map((c) => (
            <button key={c} type="button" className={`chip${c.toLowerCase() === category.toLowerCase() ? " selected" : ""}`} onClick={() => setCategory(c)}>
              <CatMark category={c} size={14} /> {c}
            </button>
          ))}
        </div>
        <div className="fitcheck-inputs">
          <input className="fitcheck-brand" placeholder="Brand (optional) — e.g. Zara" value={brand} onChange={(e) => setBrand(e.target.value)} />
          <input className="fitcheck-size" placeholder="Size on the label — e.g. 116" value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} />
        </div>

        {asked && (
          <div className={`fitcheck-verdict fitcheck-${res.verdict}`}>
            <div className="fitcheck-verdict-label">{VERDICT_LABEL[res.verdict]}</div>
            <div className="fitcheck-verdict-body">{verdictSentence(res, child.name, category, brand, sizeLabel)}</div>
            {res.brandLabel && (
              <div className="fitcheck-brandnote">
                You last logged {brand.trim()} {category.toLowerCase()} at <span className="tnum">{res.brandLabel}</span>{res.brandNote ? ` — "${res.brandNote}"` : ""}.
              </div>
            )}
            <button className="btn btn-sm fitcheck-log row" style={{ gap: "0.35rem" }}
              onClick={() => onLog({ category, brand: brand.trim(), sizeLabel: sizeLabel.trim(), sizeValue: res.value != null ? String(res.value) : "" })}>
              <Plus size={13} /> Log this size for {child.name}
            </button>
          </div>
        )}
      </div>

      {/* The reference ledger the verdict draws on (demoted from being the lead). */}
      {current.length > 0 && (
        <div className="now-next-list" style={{ marginTop: "1rem" }}>
          <div className="fitcheck-ref-head">{child.name}'s current sizes</div>
          {current.map((r) => {
            const next = nextFor.get(r.category.toLowerCase());
            return (
              <div className="now-next-row" key={r.id}>
                <CatMark category={r.category} />
                <div className="now-next-body">
                  <div className="now-next-cat">{r.category}</div>
                  <div className="now-next-sentence">
                    <span className="now-val tnum">{r.size_label}</span> now
                    {next && <> · <span style={{ color: "var(--ink-3)" }}>{next.size_label} ready</span></>}
                  </div>
                </div>
                <button className="now-next-log" onClick={() => onLog({ category: r.category })}>Log</button>
              </div>
            );
          })}
        </div>
      )}

      <div className="row" style={{ gap: "1rem", marginTop: "0.85rem", paddingTop: "0.7rem", borderTop: "1px solid var(--border-subtle)" }}>
        <button className="btn-ghost" style={{ padding: "0.2rem 0.1rem", minHeight: 0, fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent-deep)" }} onClick={onGrowth}>Growth record ›</button>
        <button className="btn-ghost" style={{ padding: "0.2rem 0.1rem", minHeight: 0, fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent-deep)" }} onClick={onReady}>Ready for next ({inventory.length}) ›</button>
      </div>
    </div>
  );
}

// ── The growth record — per-category size history as pencil rungs up the frame ─
function GrowthRecord({ records, onEdit, onDelete }: {
  records: SizeRecord[]; onEdit: (r: SizeRecord) => void; onDelete: (r: SizeRecord) => void;
}) {
  const cats = useMemo(() => categoriesIn(records), [records]);
  const [cat, setCat] = useState<string | null>(null);
  const effective = cat && cats.some((c) => c.toLowerCase() === cat.toLowerCase()) ? cat : (cats[0] ?? null);
  const filtered = useMemo(
    () => records.filter((r) => effective && r.category.toLowerCase() === effective.toLowerCase()).slice(0, 40),
    [records, effective],
  );

  // Pace line — observational only ("about every N months"), never a prediction. Needs ≥3 dated,
  // numbered sizes and at least a whole size of movement to say anything.
  const pace = useMemo(() => {
    const withVal = filtered.filter((r) => r.size_value != null).slice().sort((a, b) => a.recorded_on.localeCompare(b.recorded_on));
    if (withVal.length < 3) return null;
    const first = withVal[0], last = withVal[withVal.length - 1];
    const gained = last.size_value! - first.size_value!;
    if (gained < 1) return null;
    const months = monthsSince(first.recorded_on, new Date(last.recorded_on));
    const per = Math.round(months / gained);
    if (per < 1) return null;
    return { per, count: withVal.length };
  }, [filtered]);

  if (!effective) return null;

  return (
    <div className="card" style={{ padding: "1.1rem 1.2rem" }}>
      {cats.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1rem" }}>
          {cats.map((c) => (
            <button key={c} type="button" className={`chip${c.toLowerCase() === effective.toLowerCase() ? " selected" : ""}`} onClick={() => setCat(c)}>
              <CatMark category={c} size={14} /> {c}
            </button>
          ))}
        </div>
      )}

      {pace && (
        <>
          <div className="growth-pace">
            <ThemedArt light={glyphGrowth} dark={glyphGrowthDark} className="row" />
            {effective} have moved a size about every {pace.per === 1 ? "month" : `${pace.per} months`}.
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", margin: "-0.5rem 0 1rem 1.75rem" }}>
            Based on {pace.count} sizes logged for {effective.toLowerCase()} — observational, not a growth prediction.
          </div>
        </>
      )}

      <div className="growth-record">
        {filtered.map((r) => (
          <div className="growth-rung" key={r.id} onClick={() => onEdit(r)} style={{ cursor: "pointer" }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}>
              <div style={{ minWidth: 0 }}>
                <div className="growth-rung-size tnum">{r.size_label}</div>
                <div className="growth-rung-meta">
                  {prettyDate(r.recorded_on)}{r.brand ? ` · ${r.brand}` : ""}{r.note ? ` · ${r.note}` : ""}
                </div>
              </div>
              <button className="btn-ghost" title="Delete" aria-label="Delete size" style={{ padding: "0.3rem 0.4rem", minHeight: 0, flexShrink: 0 }}
                onClick={(ev) => { ev.stopPropagation(); onDelete(r); }}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function monthsSince(iso: string, today = new Date()): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return 0;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = (today.getTime() - d.getTime()) / 86400000;
  return Math.max(0, days / 30.4375);
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, count, action }: { title: string; count: number; action?: React.ReactNode }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", margin: "1.5rem 0 0.75rem" }}>
      <div className="row" style={{ gap: "0.4rem" }}>
        <h2 style={{ fontSize: "1rem", margin: 0 }}>{title}</h2>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{count}</span>
      </div>
      <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
        {action}
        <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on this device</span>
      </div>
    </div>
  );
}

// ── The ONE headline AI feature: Sizing Recap (observational, sizes-only) ──────
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function SizingRecapCard({ child, records, inventory }: { child: Child; records: SizeRecord[]; inventory: InventoryItem[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<ChildSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  // Reset whenever the active child changes — the recap is per child.
  useEffect(() => { setText(null); setSignals(null); setErr(null); }, [child.id]);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      const sig = await store.computeSignals(child.id, todayLocal());
      const r = await sizingRecap(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged yet to recap — add a few more sizes over time." });
      } else {
        setText(recap);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (records.length === 0) return null;

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)", marginBottom: "0.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Sizing Recap
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap sizes"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Everything logged for {child.name} — current size in each category, how fast {child.name} is moving through sizes, and which bought-ahead pieces are ready.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading {child.name}'s sizes…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
        </div>
      )}

      {err && !busy && <BrainErrorView err={err} onRetry={run} />}

      {text && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} inventory={inventory} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes the sizes you logged — sizing logistics only, not health, growth, or medical advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Second AI feature: Ask about the sizes (one billed call per question) ──────
function AskCard({ child, records, inventory }: { child: Child; records: SizeRecord[]; inventory: InventoryItem[] }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  useEffect(() => { setQ(""); setAnswer(null); setErr(null); }, [child.id]);

  async function ask() {
    const question = q.trim();
    if (!question) return;
    setBusy(true); setErr(null); setAnswer(null);
    try {
      const sig = await store.computeSignals(child.id, todayLocal());
      const r = await askSizes(sig, question);
      const a = r.answer.trim();
      if (!a) setErr({ code: "empty", message: "No answer came back — try rephrasing the question." });
      else setAnswer(a);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Ask failed — please try again." });
    } finally { setBusy(false); }
  }

  if (records.length === 0) return null;
  const examples = [`What size shoes does ${child.name} wear now?`, "Which brands did I note run small?", "Does anything I bought ahead fit yet?"];

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", margin: "0.75rem 0 0.25rem" }}>
      <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem" }}>
        <Send size={13} style={{ color: "var(--accent)" }} /> Shop-floor assistant — ask about {child.name}'s sizes
      </div>
      <form className="row" style={{ gap: "0.4rem", marginTop: "0.6rem" }} onSubmit={(e) => { e.preventDefault(); void ask(); }}>
        <input placeholder="e.g. what size does she wear in Zara?" value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: "var(--fs-sm)" }} />
        <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", flexShrink: 0 }} disabled={busy || !q.trim()}>{busy ? "…" : "Ask"}</button>
      </form>

      {!busy && !answer && !err && (
        <div className="row" style={{ gap: "0.3rem", marginTop: "0.5rem" }}>
          {examples.map((ex) => (
            <button key={ex} className="chip" type="button" onClick={() => setQ(ex)}>{ex}</button>
          ))}
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.6rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "80%" }} />
        </div>
      )}

      {err && !busy && <BrainErrorView err={err} onRetry={() => void ask()} />}

      {answer && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="muted" style={{ fontSize: 10, marginTop: 8 }}>Answers from your logged sizes only — sizing logistics, never health or growth advice.</div>
        </div>
      )}
    </div>
  );
}

function BrainErrorView({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(201,138,58,0.1)", border: "1px solid rgba(201,138,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep using AI</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from — purely observational.
function SignalEvidence({ signals, inventory }: { signals: ChildSignals; inventory: InventoryItem[] }) {
  const chips: { label: string; title: string }[] = [];

  const fastest = signals.velocities[0];
  if (fastest) {
    const sign = fastest.sizes_per_month >= 0 ? "+" : "";
    chips.push({
      label: `${fastest.category} ${sign}${fastest.sizes_per_month.toFixed(2)}/mo`,
      title: `${fastest.category}: ${fastest.earliest_label}→${fastest.latest_label} over ${fastest.months_elapsed} months (${sign}${fastest.sizes_gained} sizes)`,
    });
  }

  const brand = signals.brand_discrepancies[0];
  if (brand) {
    chips.push({
      label: `${brand.brand_a} vs ${brand.brand_b} ${brand.size_gap}`,
      title: `${brand.category}: ${brand.brand_a} ${brand.size_a_label} vs ${brand.brand_b} ${brand.size_b_label} — ${brand.size_gap} apart`,
    });
  }

  const fitsSoon = signals.ahead_bought.find((a) => a.fit === "ahead" && a.sizes_ahead != null);
  if (fitsSoon) {
    chips.push({
      label: `${fitsSoon.category} +${fitsSoon.sizes_ahead}`,
      title: `Bought-ahead ${fitsSoon.category} size ${fitsSoon.size_label} is ${fitsSoon.sizes_ahead} ahead of the current ${fitsSoon.current_size_label ?? "?"}`,
    });
  }

  const fitsNow = signals.ahead_bought.filter((a) => a.fit === "fits now").length;
  if (fitsNow > 0 && inventory.length > 0) {
    chips.push({ label: `${fitsNow} fits now`, title: `${fitsNow} bought-ahead item${fitsNow === 1 ? "" : "s"} match the current size` });
  }

  if (signals.overall.days_span > 0) {
    chips.push({ label: `${signals.overall.total_records} over ${Math.round(signals.overall.days_span / 30.4375)}mo`, title: `${signals.overall.total_records} size records spanning ${signals.overall.days_span} days` });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── Inventory tile ────────────────────────────────────────────────────────────
// Compress a picked image to a small on-device JPEG data-URL (never uploaded; the recap only
// ever sees aggregates). Fleet pattern (oriora_closet / oriora_reefline).
function compressImageToDataUrl(file: File, onDone: (dataUrl: string) => void, onErr: () => void) {
  const reader = new FileReader();
  reader.onload = () => {
    const src = reader.result;
    if (typeof src !== "string") { onErr(); return; }
    const img = new Image();
    img.onload = () => {
      const MAX = 1024;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { onDone(src); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try { onDone(canvas.toDataURL("image/jpeg", 0.82)); } catch { onDone(src); }
    };
    img.onerror = () => onDone(src);
    img.src = src;
  };
  reader.onerror = onErr;
  reader.readAsDataURL(file);
}

function InventoryTile({ i, currentValue, onEdit, onDelete }: { i: InventoryItem; currentValue: number | null; onEdit: () => void; onDelete: () => void }) {
  const [viewing, setViewing] = useState(false);
  const sizesAhead = (i.size_value != null && currentValue != null) ? Math.round((i.size_value - currentValue) * 100) / 100 : null;
  const fit: "fits now" | "ahead" | "outgrown" | "unknown" =
    sizesAhead == null ? "unknown" : sizesAhead > 0.25 ? "ahead" : sizesAhead < -0.25 ? "outgrown" : "fits now";
  const pillClass = fit === "fits now" ? "now" : fit === "ahead" ? "ahead" : fit === "outgrown" ? "outgrown" : "unknown";
  const pillText = fit === "ahead" ? "next size — ready" : fit === "fits now" ? "fits now" : fit === "outgrown" ? "outgrown" : `no ${i.category.toLowerCase()} size yet`;
  return (
    <div className="ready-item" style={{ cursor: "pointer", position: "relative" }} onClick={onEdit}>
      {i.photo_ref ? (
        <img src={i.photo_ref} alt={i.category} onClick={(ev) => { ev.stopPropagation(); setViewing(true); }} style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", cursor: "zoom-in" }} />
      ) : (
        // No photo → a short strip carrying the drawn category mark, not a full square (a 1:1
        // placeholder cost as much vertical space as a real photo).
        <div className="ready-item-photo" style={{ aspectRatio: "auto", height: "3.4rem" }}><CatMark category={i.category} size={26} /></div>
      )}
      {viewing && i.photo_ref && <PhotoViewer photos={[{ src: i.photo_ref, alt: i.category }]} onClose={() => setViewing(false)} />}
      <div className="ready-item-body">
        {/* The description is what the parent actually typed ("Navy school shoes") — lead with it. */}
        <div className="ready-item-title">{i.description?.trim() || i.category}</div>
        <div className="ready-item-meta">
          {i.category} <span className="tnum">{i.size_label}</span>{i.for_season ? ` · ${i.for_season}` : ""}
        </div>
        <div style={{ marginTop: 5 }}>
          <span className={`pill ${pillClass}`}>{pillText}</span>
        </div>
      </div>
      <button className="btn" title="Delete" aria-label="Delete item"
        style={{ position: "absolute", top: 5, right: 5, padding: "0.25rem 0.32rem", border: "none", background: "rgba(20,16,10,0.42)", color: "#fff", borderRadius: 8, minHeight: 0 }}
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={13} />
      </button>
    </div>
  );
}

// ── Forms ─────────────────────────────────────────────────────────────────────
function ChildForm({ editing, onClose, onSaved }: { editing: Child | null; onClose: () => void; onSaved: (savedId?: string) => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [birthYear, setBirthYear] = useState(editing?.birth_year != null ? String(editing.birth_year) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const thisYear = new Date().getFullYear();

  async function save() {
    if (!name.trim()) { setErr("Add a name."); return; }
    const by = birthYear.trim() ? Number(birthYear.trim()) : null;
    if (by != null && (!Number.isInteger(by) || by < thisYear - 25 || by > thisYear)) { setErr("Birth year looks off — leave it blank if unsure."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) { await store.updateChild(editing!.id, { name: name.trim(), birth_year: by }); onSaved(editing!.id); }
      else { const c = await store.createChild({ name: name.trim(), birth_year: by }); onSaved(c.id); }
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit child" : "Add a child"} onCancel={onClose}>
      <Field label="Name">
        <input autoFocus placeholder="e.g. Maya" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Birth year" optional hint="Optional — only used to label sizes with a plain age. Never health.">
        <input inputMode="numeric" placeholder={`e.g. ${thisYear - 5}`} value={birthYear} onChange={(e) => setBirthYear(e.target.value.replace(/[^0-9]/g, ""))} />
      </Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Add child"} />
    </ModalShell>
  );
}

function SizeForm({ child, editing, initialCategory, initialBrand, initialSizeLabel, initialSizeValue, categories, onClose, onSaved }: {
  child: Child; editing: SizeRecord | null; initialCategory?: string | null;
  initialBrand?: string; initialSizeLabel?: string; initialSizeValue?: string;
  categories: string[]; onClose: () => void; onSaved: () => void;
}) {
  const isEditing = editing !== null;
  const [category, setCategory] = useState(editing?.category ?? initialCategory ?? categories[0] ?? "Shoes");
  const [customCat, setCustomCat] = useState("");
  const [sizeLabel, setSizeLabel] = useState(editing?.size_label ?? initialSizeLabel ?? "");
  const [sizeValue, setSizeValue] = useState(editing?.size_value != null ? String(editing.size_value) : (initialSizeValue ?? ""));
  const [brand, setBrand] = useState(editing?.brand ?? initialBrand ?? "");
  const [recordedOn, setRecordedOn] = useState(editing?.recorded_on ?? todayLocal());
  const [note, setNote] = useState(editing?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effectiveCat = (customCat.trim() || category).trim();

  async function save() {
    if (!effectiveCat) { setErr("Pick or type a category."); return; }
    if (!sizeLabel.trim()) { setErr("Add the size (as it reads on the label)."); return; }
    const sv = sizeValue.trim() ? Number(sizeValue.trim()) : null;
    if (sv != null && Number.isNaN(sv)) { setErr("Size number must be numeric — or leave it blank."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) {
        await store.updateSizeRecord(editing!.id, { category: effectiveCat, size_label: sizeLabel.trim(), size_value: sv, brand: brand.trim(), recorded_on: recordedOn, note: note.trim() });
      } else {
        await store.createSizeRecord({ child_id: child.id, category: effectiveCat, size_label: sizeLabel.trim(), size_value: sv, brand: brand.trim(), recorded_on: recordedOn, note: note.trim() });
      }
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit size" : `Log a size for ${child.name}`} onCancel={onClose} wide>
      <Field label="Category">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.4rem" }}>
          {categories.map((c) => (
            <button key={c} type="button" className={`chip${!customCat.trim() && category.toLowerCase() === c.toLowerCase() ? " selected" : ""}`} onClick={() => { setCategory(c); setCustomCat(""); }}>
              <CatMark category={c} size={14} /> {c}
            </button>
          ))}
        </div>
        <input placeholder="…or type a new category" value={customCat} onChange={(e) => setCustomCat(e.target.value)} style={{ fontSize: "var(--fs-xs)" }} />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Size (as labelled)">
            <input placeholder="e.g. 13, 5T, EU 32" value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Number" optional hint="For change-over-time math.">
            <input inputMode="decimal" placeholder="e.g. 13" value={sizeValue} onChange={(e) => setSizeValue(e.target.value.replace(/[^0-9.]/g, ""))} />
          </Field>
        </div>
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Brand" optional>
            <input placeholder="e.g. Clarks" value={brand} onChange={(e) => setBrand(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Date">
            <input type="date" value={recordedOn} onChange={(e) => setRecordedOn(e.target.value)} />
          </Field>
        </div>
      </div>

      <Field label="Note" optional hint="e.g. runs small, gift, hand-me-down.">
        <textarea rows={2} placeholder="Anything to remember about the fit" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Save size"} />
    </ModalShell>
  );
}

function InventoryForm({ child, editing, categories, onClose, onSaved }: {
  child: Child; editing: InventoryItem | null; categories: string[]; onClose: () => void; onSaved: () => void;
}) {
  const isEditing = editing !== null;
  const [category, setCategory] = useState(editing?.category ?? categories[0] ?? "Coats");
  const [customCat, setCustomCat] = useState("");
  const [sizeLabel, setSizeLabel] = useState(editing?.size_label ?? "");
  const [sizeValue, setSizeValue] = useState(editing?.size_value != null ? String(editing.size_value) : "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [forSeason, setForSeason] = useState(editing?.for_season ?? "");
  const [boughtOn, setBoughtOn] = useState(editing?.bought_on ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Photo of the garment — compressed data-URL kept on-device (never uploaded).
  const photoInputRef = useRef<HTMLInputElement>(null);
  async function pickPhoto() {
    setErr(null);
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const p = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "gif", "webp", "heic"] }] });
      if (typeof p === "string") setPhotoRef(p);
    } catch { /* cancelled */ }
  }
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    compressImageToDataUrl(file, setPhotoRef, () => setErr("Could not read that photo — try another."));
  }

  const effectiveCat = (customCat.trim() || category).trim();

  async function save() {
    if (!effectiveCat) { setErr("Pick or type a category."); return; }
    if (!sizeLabel.trim()) { setErr("Add the size of the item."); return; }
    const sv = sizeValue.trim() ? Number(sizeValue.trim()) : null;
    if (sv != null && Number.isNaN(sv)) { setErr("Size number must be numeric — or leave it blank."); return; }
    setSaving(true); setErr(null);
    try {
      const payload = { category: effectiveCat, size_label: sizeLabel.trim(), size_value: sv, description: description.trim(), for_season: forSeason.trim(), bought_on: boughtOn.trim(), note: note.trim(), photo_ref: photoRef };
      if (isEditing) await store.updateInventoryItem(editing!.id, payload);
      else await store.createInventoryItem({ child_id: child.id, ...payload });
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit item" : `Bought ahead for ${child.name}`} onCancel={onClose} wide>
      <div className="row" style={{ gap: "0.6rem", alignItems: "center", margin: "0 0 0.6rem" }}>
        {photoRef ? (
          <img src={photoRef} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid var(--line, rgba(0,0,0,0.12))" }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "var(--elev-2, rgba(0,0,0,0.06))" }} aria-hidden><CatMark category={customCat.trim() || category} size={24} /></div>
        )}
        <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={pickPhoto}>{photoRef ? "Change photo" : "Add photo"}</button>
        {photoRef && <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => setPhotoRef("")}>Remove</button>}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>
      <Field label="Category">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.4rem" }}>
          {categories.map((c) => (
            <button key={c} type="button" className={`chip${!customCat.trim() && category.toLowerCase() === c.toLowerCase() ? " selected" : ""}`} onClick={() => { setCategory(c); setCustomCat(""); }}>
              <CatMark category={c} size={14} /> {c}
            </button>
          ))}
        </div>
        <input placeholder="…or type a new category" value={customCat} onChange={(e) => setCustomCat(e.target.value)} style={{ fontSize: "var(--fs-xs)" }} />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Size (as labelled)">
            <input placeholder="e.g. 5, 6-7y" value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Number" optional hint="So the Recap can compare to her current size.">
            <input inputMode="decimal" placeholder="e.g. 5" value={sizeValue} onChange={(e) => setSizeValue(e.target.value.replace(/[^0-9.]/g, ""))} />
          </Field>
        </div>
      </div>

      <Field label="What is it" optional>
        <input placeholder="e.g. blue winter coat" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      <Field label="For season" optional>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.4rem" }}>
          {SEASONS.map((s) => (
            <button key={s} type="button" className={`chip${forSeason === s ? " selected" : ""}`} onClick={() => setForSeason(forSeason === s ? "" : s)}>{s}</button>
          ))}
        </div>
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Bought on" optional>
            <input type="date" value={boughtOn} onChange={(e) => setBoughtOn(e.target.value)} />
          </Field>
        </div>
      </div>

      <Field label="Note" optional>
        <textarea rows={2} placeholder="Where it's stored, gift from, etc." value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Save item"} />
    </ModalShell>
  );
}

// ── Small shared pieces ───────────────────────────────────────────────────────
function Field({ label, optional, hint, children }: { label: string; optional?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>
        {label} {optional && <span className="muted" style={{ fontWeight: 400 }}>(optional)</span>}
      </div>
      {children}
      {hint && <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function FormButtons({ saving, onCancel, onSave, saveLabel }: { saving: boolean; onCancel: () => void; onSave: () => void; saveLabel: string }) {
  return (
    <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
      <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function ConfirmDelete({ title, body, onCancel, onConfirm }: { title: string; body: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <ModalShell title={title} onCancel={onCancel}>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem", lineHeight: 1.5 }}>{body}</p>
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
        <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={onConfirm}>Delete</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(20,32,43,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      {/* fill-then-cap: `width:100%` + `maxWidth:N`. The reverse (`width:N, maxWidth:100%`) resolves
          the 100% against this item's own grid track, so the card kept its full width and spilled
          off a 402px phone (the Round Card / Cellar bug, measured in the sim). */}
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 480 : 360, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
